// Columna 3 del explorador de Ventas: hero del mock con «Comprobante» tipo ticket y las cinco
// pestañas planas de Mis ventas. Recibe la venta ya resuelta, sin fetch propio de la venta.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { FileInput, Receipt } from 'lucide-react';
import { Badge } from '@/kit';
import { cn } from '@/lib/cn';
import { formatTimeMx } from '@/lib/dateHelpers';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { SaleReceiptButton, type SaleSlots } from '@/shared/patterns/SaleReceipt';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import type { BackofficeSale, SaleLogEntry } from '@/api/services/retail-wizard/contract';
import {
    SaleDetailLayoutProvider,
    salePaymentSummary,
} from '@/domains/sales/portal/mysales/components/SaleDetailSections';
import { financierChipOf } from '@/domains/sales/portal/store/lib/storeFormat';
import {
    financierKeyOf,
    financierName,
    folioTail,
    PENDING_CELL,
    saleTypeLabel,
} from '@/domains/sales/portal/mysales/lib/mySalesFormat';
import { ventaDetailFacts } from '../lib/ventaDetailFacts';
import { SALE_STATUS_DEFAULT_LABEL } from '../lib/ventasFormat';
import { VentaTab, dash } from './tabs/VentaTab';
import { ProductoTab } from './tabs/ProductoTab';
import { ClienteTab } from './tabs/ClienteTab';
import { ComisionTab } from './tabs/ComisionTab';
import { BitacoraTab } from './tabs/BitacoraTab';
import type { SaleDetailStatus } from '../lib/cancelAvailability';

export interface VentaDetailProps {
    /** La venta seleccionada, o `null` cuando no hay ninguna (estado vacío guiado). */
    sale: BackofficeSale | null;
    isLoading?: boolean;
    /** El audit trail de la venta, para el tab Bitácora. `undefined` mientras carga o si falló. */
    log?: SaleLogEntry[];
    logFailed?: boolean;
    /** Piezas de Promociones en el comprobante y en la pestaña Producto. */
    slots?: SaleSlots;
    /** Cadena consultada: la ficha del cliente se lee dentro de ella. */
    retailerId?: string | null;
    /** Lectura del detalle; el botón de cancelar espera a que llegue. */
    detailStatus?: SaleDetailStatus;
}

type TabKey = 'venta' | 'producto' | 'cliente' | 'comision' | 'bitacora';

/** Chips del hero a 9.5px, como los `chip text-[9.5px]` del mock. */
const HERO_CHIP = 'px-2 py-0.5 text-[9.5px] font-medium';

/** Variante del chip de estado; un estado nuevo del silo cae a gris con su valor crudo. */
const STATUS_BADGE: Record<string, 'success' | 'danger' | 'warning'> = {
    COMPLETED: 'success',
    CANCELLED: 'danger',
    PENDING: 'warning',
};

/** `.btn-comprobante` del mock. */
const RECEIPT_BUTTON =
    'inline-flex flex-none items-center gap-[5px] rounded-[5px] border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground transition-colors hover:border-primary/50 hover:bg-accent/50 hover:text-primary';

function VentaHero({ sale, slots }: { sale: BackofficeSale; slots: SaleSlots }) {
    const { t } = useTranslation('ventas');
    const chip = financierChipOf(sale.type, sale.externalProvider);
    const financier = financierName(financierKeyOf(sale.type, sale.externalProvider));
    // En contado el chip de financiera repetiría «Contado»: basta el del tipo.
    const plainCash = sale.type === 'CASH' && !sale.externalProvider;
    return (
        <header
            aria-label={t('detalle.encabezado', { defaultValue: 'Encabezado de la venta' })}
            className="flex-none border-b border-border px-3.5 py-3"
        >
            <div className="mb-1 flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('detalle.venta', { defaultValue: 'Venta' })}
                    </div>
                    <Badge variant="muted" className="px-2 py-0.5 text-[9px] font-medium">
                        {formatTimeMx(sale.soldAt)}
                    </Badge>
                </div>
                <SaleReceiptButton
                    sale={{
                        ...sale,
                        customerName: saleCustomerLabel(sale),
                        paymentSummary: salePaymentSummary(ventaDetailFacts(sale)),
                    }}
                    slots={slots}
                    icon={<FileInput className="size-3" aria-hidden />}
                    variant="ticket"
                    className={RECEIPT_BUTTON}
                />
            </div>
            <h2 className="m-0 truncate text-[15px] font-semibold leading-tight">
                {sale.retailCustomerId ? (
                    <Link
                        to="/retail-admin/$section"
                        params={{ section: 'clientes' }}
                        search={{ cliente: sale.retailCustomerId }}
                        title={t('detalle.fichaCliente', { defaultValue: 'Ficha cliente' })}
                        className="hover:underline"
                    >
                        {dash(saleCustomerLabel(sale))}
                    </Link>
                ) : (
                    <span className="text-muted-foreground">{saleCustomerLabel(sale)}</span>
                )}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge variant={STATUS_BADGE[sale.status] ?? 'muted'} className={HERO_CHIP}>
                    {t(`estado.${sale.status}`, {
                        defaultValue: SALE_STATUS_DEFAULT_LABEL[sale.status] ?? sale.status,
                    })}
                </Badge>
                {financier === PENDING_CELL || plainCash ? null : (
                    <Badge variant={chip.variant} className={HERO_CHIP}>
                        {financier}
                    </Badge>
                )}
                <Badge variant="muted" className={HERO_CHIP}>
                    {saleTypeLabel(sale.type)}
                </Badge>
            </div>
            <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[10.5px] text-muted-foreground">
                <span title={sale.folio} className="flex-none font-mono text-foreground">
                    {folioTail(sale.folio)}
                </span>
                <span>·</span>
                <span className="flex-none">{dash(sale.storeName)}</span>
                <span>·</span>
                <span title={sale.sellerName ?? undefined} className="truncate">
                    {t('detalle.vendedorPrefijo', { defaultValue: 'Vendedor' })}{' '}
                    {dash(sale.sellerName)}
                </span>
            </div>
        </header>
    );
}

export function VentaDetail({
    sale,
    isLoading = false,
    log,
    logFailed,
    slots = {},
    retailerId = null,
    detailStatus,
}: VentaDetailProps) {
    const { t } = useTranslation('ventas');
    const [tab, setTab] = useState<TabKey>('venta');

    const tabs: { key: TabKey; label: string }[] = [
        { key: 'venta', label: t('tabs.venta', { defaultValue: 'Venta' }) },
        { key: 'producto', label: t('tabs.producto', { defaultValue: 'Producto' }) },
        { key: 'cliente', label: t('tabs.cliente', { defaultValue: 'Cliente' }) },
        { key: 'comision', label: t('tabs.comision', { defaultValue: 'Comisión' }) },
        { key: 'bitacora', label: t('tabs.bitacora', { defaultValue: 'Bitácora' }) },
    ];

    if (isLoading) {
        return (
            <div role="status" className="flex flex-1 flex-col">
                <MasterDetailEmpty
                    icon={<Receipt className="size-10" aria-hidden />}
                    title={t('detalle.cargando', { defaultValue: 'Cargando venta…' })}
                />
            </div>
        );
    }

    if (!sale) {
        return (
            <MasterDetailEmpty
                icon={<Receipt className="size-10" aria-hidden />}
                title={t('detalle.vacioTitulo', { defaultValue: 'Ninguna venta seleccionada' })}
                sub={t('detalle.vacioDescripcion', {
                    defaultValue: 'Elige una fila de la lista para ver su detalle.',
                })}
            />
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <VentaHero sale={sale} slots={slots} />

            <div
                role="tablist"
                className="flex flex-none gap-0.5 overflow-x-auto border-b border-border px-2 py-1.5"
            >
                {tabs.map(({ key, label }) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={tab === key}
                        onClick={() => {
                            setTab(key);
                        }}
                        className={cn(
                            'whitespace-nowrap rounded-[5px] px-2.5 py-[5px] text-[11.5px] transition-colors',
                            tab === key
                                ? 'bg-accent font-semibold text-foreground'
                                : 'font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                        )}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <SaleDetailLayoutProvider value="flat">
                <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
                    {tab === 'venta' && (
                        <VentaTab
                            sale={sale}
                            slots={slots}
                            {...(detailStatus ? { detailStatus } : {})}
                        />
                    )}
                    {tab === 'producto' && <ProductoTab sale={sale} slots={slots} />}
                    {tab === 'cliente' && <ClienteTab sale={sale} retailerId={retailerId} />}
                    {tab === 'comision' && <ComisionTab sale={sale} />}
                    {tab === 'bitacora' && (
                        <BitacoraTab sale={sale} log={log} logFailed={logFailed} />
                    )}
                </div>
            </SaleDetailLayoutProvider>
        </div>
    );
}
